import React, { useEffect, useState, useMemo } from "react";
import { useQuery } from "react-query";
import { fetchDataFrom } from "../../_helper/fetcher";
import { dateFormat, priceFormat } from "../../_helper/helper";

export default function InvoicePrint({ order }) {
  const [freeOnes, setFreeOnes] = useState([]);
  
  // Fetch regions and cities
  const fetchRegions = async () => {
    const res = await fetchDataFrom("regions?limit=1000");
    if (res.error) return [];
    return res.data || [];
  };

  const fetchCities = async () => {
    const res = await fetchDataFrom("cities?limit=1000");
    if (res.error) return [];
    return res.data || [];
  };

  const { data: regions = [] } = useQuery(["regions"], fetchRegions, {
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  const { data: cities = [] } = useQuery(["cities"], fetchCities, {
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  // Helper functions to get region and city names
  const getRegionName = useMemo(() => {
    return (regionId) => {
      if (!regionId) return "";
      // Check if region is already an object with name
      if (typeof regionId === "object" && regionId?.name) {
        return regionId.name;
      }
      // Lookup by ID
      const region = regions.find((r) => r.id === regionId);
      return region?.name || "";
    };
  }, [regions]);

  const getCityName = useMemo(() => {
    return (cityId) => {
      if (!cityId) return "";
      // Check if city is already an object with name
      if (typeof cityId === "object" && cityId?.name) {
        return cityId.name;
      }
      // Lookup by ID
      const city = cities.find((c) => c.id === cityId);
      return city?.name || "";
    };
  }, [cities]);

  useEffect(() => {
    let free = [...order?.info?.map((item) => item?.get_y_products)];
    if (free[0]) {
      if (order?.coupon_promo && order?.coupon_promo?.products?.length > 0) {
        free = [...free, ...order?.coupon_promo?.products];
      }
      setFreeOnes(free[0]);
    }
  }, [order]);

  // Get billing region and city names
  // Check if region/city exist as objects first, otherwise use IDs
  // If city has region data, derive region from city
  const billingCityName = useMemo(() => {
    // If billing_city exists as an object with name, use it
    if (order?.billing_city?.name) {
      return order.billing_city.name;
    }
    // Otherwise, try to lookup by ID
    if (order?.billing_city_id) {
      return getCityName(order.billing_city_id);
    }
    return "";
  }, [order?.billing_city, order?.billing_city_id, getCityName]);

  const billingRegionName = useMemo(() => {
    // First, check if billing_region exists as an object with name
    if (order?.billing_region?.name) {
      return order.billing_region.name;
    }
    // Second, check if billing_city has region data (cities belong to regions)
    if (order?.billing_city?.region?.name) {
      return order.billing_city.region.name;
    }
    // Third, try to get region from city lookup (cities fetched have region property)
    // Get city ID from either billing_city_id or billing_city.id
    const cityId = order?.billing_city_id || order?.billing_city?.id;
    if (cityId && cities.length > 0) {
      const city = cities.find((c) => c.id === cityId || c.id === parseInt(cityId));
      if (city?.region?.name) {
        return city.region.name;
      }
    }
    // Fourth, try to lookup by region ID
    const regionId = order?.billing_region_id || order?.billing_region?.id;
    if (regionId) {
      return getRegionName(regionId);
    }
    return "";
  }, [order?.billing_region, order?.billing_region_id, order?.billing_city, order?.billing_city_id, getRegionName, cities]);

  // Get shipping region and city names
  const shippingCityName = useMemo(() => {
    if (order?.diff_area_status === 1) {
      // If shipping_city exists as an object with name, use it
      if (order?.shipping_city?.name) {
        return order.shipping_city.name;
      }
      // Otherwise, try to lookup by ID
      if (order?.shipping_city_id) {
        return getCityName(order.shipping_city_id);
      }
      return "";
    }
    return billingCityName;
  }, [order?.diff_area_status, order?.shipping_city, order?.shipping_city_id, getCityName, billingCityName]);

  const shippingRegionName = useMemo(() => {
    if (order?.diff_area_status === 1) {
      // First, check if shipping_region exists as an object with name
      if (order?.shipping_region?.name) {
        return order.shipping_region.name;
      }
      // Second, check if shipping_city has region data (cities belong to regions)
      if (order?.shipping_city?.region?.name) {
        return order.shipping_city.region.name;
      }
      // Third, try to get region from city lookup (cities fetched have region property)
      // Get city ID from either shipping_city_id or shipping_city.id
      const cityId = order?.shipping_city_id || order?.shipping_city?.id;
      if (cityId && cities.length > 0) {
        const city = cities.find((c) => c.id === cityId || c.id === parseInt(cityId));
        if (city?.region?.name) {
          return city.region.name;
        }
      }
      // Fourth, try to lookup by region ID
      const regionId = order?.shipping_region_id || order?.shipping_region?.id;
      if (regionId) {
        return getRegionName(regionId);
      }
      return "";
    }
    return billingRegionName;
  }, [order?.diff_area_status, order?.shipping_region, order?.shipping_region_id, order?.shipping_city, order?.shipping_city_id, getRegionName, billingRegionName, cities]);

  let shipment = {
    name: order?.billing_name,
    address: order?.billing_address,
    phone: order?.billing_phone,
    email: order?.billing_email,
    township: order?.billing_township?.name,
    city: billingCityName,
    region: billingRegionName,
  };
  if (order?.diff_area_status === 1) {
    shipment = {
      name: order?.shipping_name,
      address: order?.shipping_address,
      phone: order?.shipping_phone,
      email: order?.shipping_email,
      township: order?.shipping_township?.name,
      city: shippingCityName,
      region: shippingRegionName,
    };
  }
  return (
    <>
      {/**
       * Calculate totals for invoice display
       * - originalTotal: sum of unit price * quantity (before any discounts)
       * - discountedTotal: order summary after discounts
       * - discountAmount: originalTotal - discountedTotal (never negative)
       */}
      {(() => {
        return null;
      })()}
      {
        // derive totals used below
      }
      {(() => {
        return null;
      })()}
      <div className="w-full grid grid-cols-2 mb-4">
        <div className="text-left">
          <img src="/image/logo2.png" className="w-[10rem]" alt="" />
        </div>
        <div className="text-right">
          <p className="text-xl font-bold">OGO Home Improvement</p>
          <p>09880566420</p>
          <p className="whitespace-nowrap">
            No-16/A Oke Kyin Bu Tar Yon St, <br />
            Yangon, Myanmar(Burma)
          </p>
        </div>
      </div>
      <div className="font-bold grid grid-cols-2 w-max mb-4 gap-1">
        <p>Invoice </p>
        <p>: {order?.id}</p>
        <p>Order Number </p>
        <p>: {order?.order_number}</p>
        <p>Order Date </p>
        <p>: {dateFormat(order?.created_at, "full")}</p>
        <p>Payment Method </p>
        <p>: {order?.payment_method?.split("_")?.join(" ")}</p>
        <p>Shpping Method </p>
        <p>
          : {order?.diff_area_status === 1 ? "Drop Shipping" : "Door to door"}
        </p>
      </div>

      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-logo_color font-bold">
            <td className="text-center p-3 border border-[#000] border-b-0">
              Billing Address
            </td>
            <td className="text-center p-3 border border-[#000] border-b-0 border-l-0">
              Shipping Address
            </td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="text-left pl-2 border border-[#000] py-2">
              <ul className="flex flex-col gap-2">
                <li className="flex items-center">
                  <div className="font-bold w-20">Name</div>:
                  <span className="ml-4">{order?.billing_name}</span>
                </li>
                <li className="flex items-center">
                  <div className="font-bold w-20">Phone</div>:
                  <span className="ml-4">{order?.billing_phone}</span>
                </li>
                <li className="flex items-center">
                  <div className="font-bold w-20">Email</div>:
                  <span className="ml-4">{order?.billing_email}</span>
                </li>
                <li className="flex items-start">
                  <div className="font-bold min-w-[5rem]">Address</div>:
                  <span className="ml-4 text-left">
                    {order?.billing_address}
                    {order?.billing_township?.name && (
                      <>, {order?.billing_township?.name}</>
                    )}
                  </span>
                </li>
                {billingRegionName && (
                  <li className="flex items-center">
                    <div className="font-bold w-20">Region</div>:
                    <span className="ml-4">{billingRegionName}</span>
                  </li>
                )}
                {billingCityName && (
                  <li className="flex items-center">
                    <div className="font-bold w-20">City</div>:
                    <span className="ml-4">{billingCityName}</span>
                  </li>
                )}
              </ul>
            </td>
            <td className="text-left pl-2 border border-[#000] py-2">
              <ul className="flex flex-col gap-2">
                <li className="flex items-center">
                  <div className="font-bold w-20">Name</div>:
                  <span className="ml-4">{shipment.name}</span>
                </li>
                <li className="flex items-center">
                  <div className="font-bold w-20">Phone</div>:
                  <span className="ml-4">{shipment.phone}</span>
                </li>
                <li className="flex items-center">
                  <div className="font-bold w-20">Email</div>:
                  <span className="ml-4">{shipment.email}</span>
                </li>
                <li className="flex items-start">
                  <div className="font-bold min-w-[5rem]">Address</div>:
                  <span className="ml-4 text-left">
                    {shipment.address}
                    {shipment.township && (
                      <>, {shipment.township}</>
                    )}
                  </span>
                </li>
                {shipment.region && (
                  <li className="flex items-center">
                    <div className="font-bold w-20">Region</div>:
                    <span className="ml-4">{shipment.region}</span>
                  </li>
                )}
                {shipment.city && (
                  <li className="flex items-center">
                    <div className="font-bold w-20">City</div>:
                    <span className="ml-4">{shipment.city}</span>
                  </li>
                )}
                {order?.drop_shipping?.bus_gate && (
                  <li className="flex items-start">
                    <div className="font-bold w-20">Bus Gate</div>:
                    <span className="ml-4">
                      {order?.drop_shipping?.bus_gate}
                    </span>
                  </li>
                )}
              </ul>
            </td>
          </tr>
        </tbody>
      </table>

      <table className="w-full border-collapse mt-4">
        <thead>
          <tr>
            <td className="text-left font-bold border pl-4 border-[#000] py-1">
              SKU Code
            </td>
            <td className="text-left font-bold border pl-4 border-[#000] py-1">
              Item
            </td>
            <td className="text-left font-bold border pl-4 border-[#000] py-1">
              Price
            </td>
            <td className="text-left font-bold border pl-4 border-[#000] py-1">
              Quantity
            </td>
            <td className="text-left font-bold border pl-4 border-[#000] py-1">
              Discount
            </td>
            <td className="text-left font-bold border pl-4 border-[#000] py-1">
              Total Price
            </td>
          </tr>
        </thead>
        <tbody>
        {order?.info?.map((item, index) => {
          // Safely handle cases where sku might be null/undefined
          if (!item || !item.sku) {
            return null;
          }

          const { sku, summary_price, product_promo, quantity } = item;
          const { product, price } = sku || {};

          return (
            <tr key={item.id || index}>
                <td className="text-left pl-4 py-1 border border-[#000] text-sm w-max">
                  {item?.sku?.code}
                </td>
                <td className="text-left pl-4 py-1 border border-[#000]">
                  {product?.name}
                </td>
                <td className="text-left pl-4 py-1 border border-[#000]">
                  {priceFormat(price)} Ks
                </td>
                <td className="text-left pl-4 py-1 border border-[#000]">
                  {quantity}
                </td>
                <td className="text-left pl-4 py-1 border border-[#000]">
                  {priceFormat(product_promo || 0)}
                </td>
                <td className="text-left pl-4 py-1 border border-[#000]">
                  {priceFormat(summary_price)} Ks
                </td>
              </tr>
            );
          })}

          {(() => {
            // Use order-level prices directly from API response
            // Note: summary_price already contains delivery_price
            const originalPrice = Number(order?.original_price) || 0;
            const summaryPrice = Number(order?.summary_price) || 0;
            const deliveryPrice = Number(order?.delivery_price) || 0;
            
            // Calculate discount: original_price - (summary_price - delivery_price)
            const discountPrice = originalPrice - (summaryPrice - deliveryPrice);
            
            // Grand total is the summary_price directly
            const grandTotal = summaryPrice;

            return (
              <>
                <tr>
                  <td colSpan="3"></td>
                  <td
                    colSpan="2"
                    className="pl-4 font-semibold py-1 border border-[#000]"
                  >
                    Subtotal
                  </td>
                  <td className="text-left pl-4 py-1 border border-[#000]">
                    {priceFormat(originalPrice)} Ks
                  </td>
                </tr>
                {discountPrice > 0 && (
                  <tr>
                    <td colSpan="3"></td>
                    <td
                      colSpan="2"
                      className="pl-4 font-semibold py-1 border border-[#000]"
                    >
                      Discount
                    </td>
                    <td className="text-left pl-4 py-1 border border-[#000]">
                      {priceFormat(discountPrice)} Ks
                    </td>
                  </tr>
                )}
                <tr>
                  <td colSpan="3"></td>
                  <td
                    colSpan="2"
                    className="pl-4 font-semibold py-1 border border-[#000]"
                  >
                    Delivery Fees
                  </td>
                  <td className="text-left pl-4 py-1 border border-[#000]">
                    {priceFormat(deliveryPrice)} Ks
                  </td>
                </tr>
                <tr>
                  <td colSpan="3"></td>
                  <td
                    colSpan="2"
                    className="pl-4 font-semibold py-1 border border-[#000]"
                  >
                    Grand Total
                  </td>
                  <td className="text-left pl-4 py-1 border border-[#000]">
                    {priceFormat(grandTotal)} Ks
                  </td>
                </tr>
              </>
            );
          })()}
        </tbody>
      </table>

      {freeOnes?.length > 0 && (
        <>
          <h4 className="mt-4 font-semibold">Gift</h4>
          <table className="w-full border-collapse ">
            <thead>
              <tr>
                <td className="text-left font-bold border pl-4 border-[#000] py-1">
                  Item
                </td>
                <td className="text-left font-bold border pl-4 border-[#000] py-1">
                  SKU ID
                </td>
                <td className="text-left font-bold border pl-4 border-[#000] py-1">
                  Unit Price
                </td>
              </tr>
            </thead>
            <tbody>
              {freeOnes?.map((item) => (
                <tr key={item.sku_id}>
                  <td className="text-left pl-4 py-1 border border-[#000]">
                    {item.name}
                  </td>
                  <td className="text-left pl-4 py-1 border border-[#000] text-sm w-max">
                    {item.sku_id}
                  </td>
                  <td className="text-left pl-4 py-1 border border-[#000]">
                    {priceFormat(item.price)} Ks
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}
